/**
 * Creator Pro — Super Admin master price editor (Phase B, Jul 2026).
 *
 * Loads the editable SKU tree from /api/admin/creator-pro/catalog and lets the
 * super admin master-edit every price (day-one + regular à-la-carte), plus
 * names / descriptions / "what it touches". The buffet checkout + CTAs read the
 * same catalog, so edits here propagate everywhere. Prices shown in dollars,
 * stored in cents.
 */
import { useEffect, useState, useCallback } from "react";
import { FloppyDisk, ArrowCounterClockwise, Lock, Warning, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LOCK_STYLE_OPTIONS, lockStyleLabel } from "@/lib/entitlements";

const d = (cents) => (cents == null ? "" : (cents / 100).toString());
const c = (dollars) => Math.max(0, Math.round((parseFloat(dollars) || 0) * 100));
const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(0)}`;

// Sum every leaf/child SKU price across the tree (the "à la carte" stack).
// Oct 1, 2026 — skip `needs_code` SKUs. The authoritative number the creator
// sees is the backend's `alacarte_total_cents`, which has always excluded
// unbuilt SKUs; this client sum did not, so the first registered-but-unbuilt
// SKU (Silo Anywhere) made the admin stat read $50 higher than the storefront
// value shown to creators. An unshipped feature carries no value yet.
function sumTree(catalog, field) {
  let total = 0;
  for (const s of catalog.suites || []) {
    for (const it of s.items || []) {
      if (!it.needs_code) total += it[field] || 0;
      for (const ch of it.children || []) {
        if (!ch.needs_code) total += ch[field] || 0;
      }
    }
  }
  return total;
}

function PriceField({ label, cents, onChange, testid, disabled }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">{label}</span>
      <div className="flex items-center gap-1">
        <span className="text-[#6B6A68] text-sm">$</span>
        <input
          type="number" min="0" step="1" inputMode="decimal"
          data-testid={testid}
          disabled={disabled}
          value={d(cents)}
          onChange={(e) => onChange(c(e.target.value))}
          className="w-20 px-2 py-1 rounded border hairline text-sm tabular-nums bg-white disabled:bg-[#F0F1EE]"
        />
      </div>
    </label>
  );
}

// One SKU row (item or child). `onField(field, value)` mutates via parent.
function SkuRow({ node, depth, onField }) {
  return (
    <div
      data-testid={`cpro-sku-${node.key}`}
      className={`rounded-md border hairline p-3 bg-white ${depth > 0 ? "ml-6 border-l-2 border-l-[#C16E5A]/40" : ""}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={node.name || ""}
              onChange={(e) => onField("name", e.target.value)}
              data-testid={`cpro-name-${node.key}`}
              className="font-medium text-sm text-[#1C1B1A] bg-transparent border-b border-transparent hover:border-[#E5E5E0] focus:border-[#C16E5A] outline-none min-w-[180px]"
            />
            {node.locked && <span className="text-[9px] uppercase tracking-[0.14em] bg-[#4A5D4E] text-white px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Lock size={9} weight="bold" /> Required</span>}
            {node.needs_code && <span className="text-[9px] uppercase tracking-[0.14em] bg-[#C16E5A] text-white px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Warning size={9} weight="bold" /> Needs code</span>}
            <span className="text-[9px] text-[#8A8A82] font-mono">{node.key}</span>
          </div>
          <textarea
            value={node.description || ""}
            onChange={(e) => onField("description", e.target.value)}
            data-testid={`cpro-desc-${node.key}`}
            rows={2}
            className="mt-2 w-full text-xs text-[#6B6A68] bg-[#FDFCF8] border hairline rounded px-2 py-1 outline-none focus:border-[#C16E5A] resize-y"
            placeholder="Customer-facing description"
          />
          {node.touches !== undefined && (
            <div className="mt-1 flex items-start gap-1 text-[10px] text-[#8A8A82]">
              <Wrench size={11} weight="duotone" className="shrink-0 mt-0.5" />
              <input
                value={node.touches || ""}
                onChange={(e) => onField("touches", e.target.value)}
                data-testid={`cpro-touches-${node.key}`}
                className="flex-1 bg-transparent border-b border-transparent hover:border-[#E5E5E0] focus:border-[#C16E5A] outline-none"
                placeholder="What this touches (internal)"
              />
            </div>
          )}
        </div>
        <div className="flex items-end gap-3">
          <PriceField label="Day-one" cents={node.day1_cents} testid={`cpro-day1-${node.key}`} onChange={(v) => onField("day1_cents", v)} />
        </div>
      </div>
    </div>
  );
}

export default function CreatorProCatalog() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/creator-pro/catalog");
      setCatalog(r.data.catalog);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Creator Pro catalog.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Immutable update by suite index / item index / (optional) child index.
  const setField = (si, ii, ci, field, value) => {
    setCatalog((prev) => {
      const next = structuredClone(prev);
      let target;
      if (ii == null) target = next.suites[si];
      else if (ci == null) target = next.suites[si].items[ii];
      else target = next.suites[si].items[ii].children[ci];
      target[field] = value;
      return next;
    });
    setDirty(true);
  };
  const setBundle = (field, value) => {
    setCatalog((prev) => ({ ...prev, bundle: { ...prev.bundle, [field]: value } }));
    setDirty(true);
  };
  const currentLockStyles = () => {
    const v = catalog?.lock_style;
    if (Array.isArray(v)) return v.length ? v : ["none"];
    if (typeof v === "string" && v) return [v];
    return ["red_shimmer"];
  };
  const setLockStyle = (v) => {
    // Jul 2026 (Randy) — styles now STACK. Toggle each on/off; `none` is
    // exclusive (means "no treatment"), and an empty selection means none.
    setCatalog((prev) => {
      let arr = Array.isArray(prev.lock_style)
        ? [...prev.lock_style]
        : (prev.lock_style ? [prev.lock_style] : ["red_shimmer"]);
      if (v === "none") {
        arr = arr.includes("none") ? ["red_shimmer"] : ["none"];
      } else {
        arr = arr.filter((x) => x !== "none");
        arr = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
        if (arr.length === 0) arr = ["none"];
      }
      return { ...prev, lock_style: arr };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/creator-pro/catalog", { catalog });
      toast.success("Creator Pro catalog saved.");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };
  const resetDefaults = async () => {
    if (!window.confirm("Reset the Creator Pro catalog to defaults? This overwrites your edits.")) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/creator-pro/catalog/reset");
      setCatalog(r.data.catalog);
      setDirty(false);
      toast.success("Catalog reset to defaults.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-[#6B6A68] text-sm py-6" data-testid="cpro-loading">Loading Creator Pro catalog…</div>;
  if (!catalog) return null;

  const day1Stack = sumTree(catalog, "day1_cents");

  return (
    <div className="space-y-6" data-testid="creator-pro-catalog-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Creator Pro — master pricing</h2>
          <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
            The single source of truth for the Creator Pro "software buffet." Edit any price, name, or description here — the checkout and every CTA read from this. Day-one = the current à-la-carte price for that SKU.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetDefaults} disabled={saving} data-testid="cpro-reset-btn"
            className="px-3 py-2 rounded-md border hairline text-sm text-[#6B6A68] hover:text-[#1C1B1A] inline-flex items-center gap-1.5 disabled:opacity-50">
            <ArrowCounterClockwise size={14} /> Reset
          </button>
          <button onClick={save} disabled={saving || !dirty} data-testid="cpro-save-btn"
            className="px-4 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
            <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {/* Bundle headline pricing + value-stack readout */}
      <div className="rounded-xl border hairline bg-[#1C1B1A] text-[#FDFCF8] p-5" data-testid="cpro-bundle">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A]">Full bundle</div>
            <input value={catalog.bundle.name || ""} onChange={(e) => setBundle("name", e.target.value)} data-testid="cpro-bundle-name"
              className="font-serif-display text-2xl bg-transparent border-b border-transparent hover:border-white/20 focus:border-[#C16E5A] outline-none" />
            <input value={catalog.bundle.tagline || ""} onChange={(e) => setBundle("tagline", e.target.value)} data-testid="cpro-bundle-tagline"
              className="block mt-1 text-sm text-[#E5E5E0] bg-transparent border-b border-transparent hover:border-white/20 focus:border-[#C16E5A] outline-none w-full max-w-md" />
          </div>
          <div className="flex items-end gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#A8A8A0]">Bundle day-one</span>
              <div className="flex items-center gap-1"><span className="text-[#A8A8A0]">$</span>
                <input type="number" min="0" value={d(catalog.bundle.day1_cents)} onChange={(e) => setBundle("day1_cents", c(e.target.value))} data-testid="cpro-bundle-day1"
                  className="w-24 px-2 py-1 rounded bg-white/10 border border-white/20 text-sm tabular-nums text-white outline-none focus:border-[#C16E5A]" /></div>
            </label>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs" data-testid="cpro-value-stack">
          <Stat label="À la carte (sum of SKUs)" value={fmt$(day1Stack)} />
          <Stat label="Bundle day-one" value={fmt$(catalog.bundle.day1_cents)} accent />
          <Stat label="Bundle saves vs à la carte" value={fmt$(Math.max(0, day1Stack - catalog.bundle.day1_cents))} accent />
        </div>
        {/* Aug 2026 (Randy pt-pay-it-forward) — platform-wide toggle. When ON,
            Creator Pros who own everything AND have an eligible referrer see
            an optional "fist-bump your referrer" button on the "View what
            you have" modal. When OFF, the whole feature vanishes. */}
        <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="cpro-pay-forward-toggle">
            <input
              type="checkbox"
              checked={catalog.pay_forward_enabled !== false}
              onChange={(e) =>
                setCatalog((prev) => ({ ...prev, pay_forward_enabled: e.target.checked }))
              }
              className="accent-[#C16E5A] w-4 h-4"
              data-testid="cpro-pay-forward-checkbox"
            />
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#E5E5E0]">Pay-It-Forward</span>
            <span className={`text-[10px] uppercase tracking-[0.14em] ${catalog.pay_forward_enabled !== false ? "text-[#CDE0CF]" : "text-[#A8A8A0]"}`}>
              {catalog.pay_forward_enabled !== false ? "ON" : "OFF"}
            </span>
          </label>
          <span className="text-[11px] text-[#A8A8A0] leading-snug">
            When ON, Creator Pros see an optional &ldquo;fist-bump your referrer&rdquo; button on the
            &ldquo;View what you have&rdquo; modal for the savings amount (33% to the referrer as a partner bonus).
          </span>
        </div>
      </div>

      {/* Suites → items → children */}
      {catalog.suites.map((s, si) => (
        <div key={s.key} className="rounded-xl border hairline bg-[#FDFCF8] p-4" data-testid={`cpro-suite-${s.key}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 flex-wrap">
                <input value={s.name || ""} onChange={(e) => setField(si, null, null, "name", e.target.value)} data-testid={`cpro-suite-name-${s.key}`}
                  className="font-serif-display text-xl text-[#1C1B1A] bg-transparent border-b border-transparent hover:border-[#E5E5E0] focus:border-[#C16E5A] outline-none" />
                {s.required && <span className="text-[9px] uppercase tracking-[0.14em] bg-[#4A5D4E] text-white px-1.5 py-0.5 rounded">Required suite</span>}
                <span className="text-[9px] text-[#8A8A82] font-mono">{s.key}</span>
              </div>
              <input value={s.description || ""} onChange={(e) => setField(si, null, null, "description", e.target.value)} data-testid={`cpro-suite-desc-${s.key}`}
                className="mt-1 w-full text-xs text-[#6B6A68] bg-transparent border-b border-transparent hover:border-[#E5E5E0] focus:border-[#C16E5A] outline-none" />
            </div>
            <div className="flex items-end gap-3">
              {/* Aug 2026 (Randy pt-suite-header-fix) — Suite header prices
                  are now AUTO-COMPUTED from the sum of their children. This
                  makes the header numbers structurally impossible to drift
                  out of sync with the individual SKUs. If you want the suite
                  header to move, change a child price and the header updates
                  live. If you want a different suite total than the sum of
                  children, that's a data model change (add a suite-level
                  markup/discount concept) — not something the editor should
                  allow via hand-typed numbers. */}
              {(() => {
                const childD1 = (s.items || []).reduce((acc, it) => {
                  const own = (it.day1_cents || 0);
                  const kids = (it.children || []).reduce((a, c) => a + (c.day1_cents || 0), 0);
                  return acc + own + kids;
                }, 0);
                return (
                  <div className="flex items-end gap-3">
                    <div className="text-right" data-testid={`cpro-suite-day1-${s.key}`}>
                      <div className="text-[9px] uppercase tracking-[0.16em] text-[#A8A8A0]">Suite day-one</div>
                      <div className="text-lg text-[#1C1B1A] tabular-nums font-medium">{fmt$(childD1)}</div>
                      <div className="text-[9px] text-[#6B6A68] italic">auto · sum of SKUs</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="space-y-2">
            {s.items.map((it, ii) => (
              <div key={it.key} className="space-y-2">
                <SkuRow node={it} depth={0} onField={(f, v) => setField(si, ii, null, f, v)} />
                {(it.children || []).map((ch, ci) => (
                  <SkuRow key={ch.key} node={ch} depth={1} onField={(f, v) => setField(si, ii, ci, f, v)} />
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Locked-feature look — how gated tools appear to non-Pro users */}
      <div className="rounded-xl border hairline bg-[#FDFCF8] p-4" data-testid="cpro-lockstyle">
        <h3 className="font-serif-display text-xl text-[#1C1B1A] flex items-center gap-2"><Lock size={18} weight="duotone" /> Locked-feature look</h3>
        <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
          How a Creator Pro tool appears to someone who hasn't unlocked it. Kept intentionally mild so a page never feels like it's blinking. Pick <strong>one or more</strong> — they stack (e.g. a frozen sheen + a soft blur + a lock badge). They all apply everywhere the moment you save.
        </p>

        {/* Combined live preview of the current stack */}
        {(() => {
          const selected = currentLockStyles();
          const stackClasses = selected.map((v) => `cp-lock--${v}`).join(" ");
          return (
            <div className="mt-4 flex items-center gap-3 rounded-lg border hairline bg-white p-3" data-testid="cpro-lockstyle-preview">
              <span className={`cp-lock ${stackClasses}`} aria-hidden="true">
                <span className="px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white text-xs">Boost</span>
                {selected.includes("lock_badge") && <span className="cp-lock-badge"><Lock size={11} weight="fill" /></span>}
              </span>
              <div className="text-[11px] text-[#8A8A82]">
                <div className="uppercase tracking-[0.14em] text-[10px] text-[#6B6A68]">Combined preview</div>
                <div className="mt-0.5">{selected.map((v) => lockStyleLabel(v)).join(" + ")}</div>
              </div>
            </div>
          );
        })()}

        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {LOCK_STYLE_OPTIONS.map((opt) => {
            const active = currentLockStyles().includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLockStyle(opt.value)}
                data-testid={`cpro-lockstyle-${opt.value}`}
                aria-pressed={active}
                className={`text-left rounded-lg border p-3 transition-colors ${active ? "border-[#C16E5A] ring-1 ring-[#C16E5A] bg-[#C16E5A]/5" : "border-[#E5E5E0] hover:border-[#C16E5A]/50 bg-white"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-[#1C1B1A]">{opt.label}</span>
                  {active && <span className="text-[9px] uppercase tracking-[0.14em] bg-[#C16E5A] text-white px-1.5 py-0.5 rounded">On</span>}
                </div>
                <p className="text-[11px] text-[#6B6A68] mt-1 min-h-[30px]">{opt.hint}</p>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`cp-lock cp-lock--${opt.value}`} aria-hidden="true">
                    <span className="px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white text-xs">Boost</span>
                    {opt.value === "lock_badge" && <span className="cp-lock-badge"><Lock size={11} weight="fill" /></span>}
                  </span>
                  <span className="text-[10px] text-[#8A8A82]">live preview</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving || !dirty} data-testid="cpro-save-btn-bottom"
          className="px-4 py-2 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-md px-3 py-2 ${accent ? "bg-[#C16E5A]/20" : "bg-white/10"}`}>
      <div className="text-[9px] uppercase tracking-[0.14em] text-[#A8A8A0]">{label}</div>
      <div className="font-serif-display text-lg tabular-nums">{value}</div>
    </div>
  );
}
